import React, { useCallback, useMemo, useState } from 'react';

import { IonChip, IonCol, IonIcon, IonRow } from '@ionic/react';
import { ellipsisHorizontal } from 'ionicons/icons';

import {
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import cloneDeep from 'lodash/cloneDeep';
import { Line } from 'react-chartjs-2';

import Button from 'components/Button';
import { chartOptions } from 'components/Chart/consts';
import { getColorByIndex } from 'components/Chart/utils/colors';
import ProgressBar from 'components/ProgressBar';
import Typography from 'components/Typography';
import { useCurrencyFormat, useNumberFormat, usePercentFormat } from 'intl';

import './index.css';

ChartJS.register(
  TimeScale,
  TimeSeriesScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Chart = ({
  isRefetching,
  data,
  onShowMetricsModal,
}: {
  isRefetching: boolean;
  data: any;
  onShowMetricsModal: (config: any) => void;
}) => {
  const [invisibleMetrics, setInvisibleMetrics] = useState([]);

  const numberFormat = useNumberFormat({
    notation: 'compact',
    compactDisplay: 'short',
  });
  const percentFormat = usePercentFormat({
    notation: 'compact',
    compactDisplay: 'short',
  });
  const currencyFormat = useCurrencyFormat({
    notation: 'compact',
    compactDisplay: 'short',
  });
  const chartMemoData: any = useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      ...data,
      datasets: data?.datasets?.map((el: any, index: never) => {
        return {
          ...el,
          yAxisID: el.metricType,
          hidden: invisibleMetrics.includes(index),
          borderColor: getColorByIndex(index),
          backgroundColor: getColorByIndex(index),
          cubicInterpolationMode: 'monotone',
          tension: 0.4,
        };
      }),
    };
  }, [data, invisibleMetrics]);

  const tooltipRender = useCallback(
    (context: any): string => {
      let label: string = context.dataset.label || '';

      if (label) {
        label += ': ';
      }
      if (context.parsed.y !== null) {
        switch (context.dataset.metricType) {
          case 'number':
            label += numberFormat(context.parsed.y);
            break;
          case 'percent':
            label += percentFormat(context.parsed.y);
            break;
          case 'currency':
            label += currencyFormat(context.parsed.y);
            break;
        }
      }
      return label;
    },
    [currencyFormat, numberFormat, percentFormat]
  );

  const chartOptionsMemo = useMemo(() => {
    const newOptions = cloneDeep(chartOptions);
    let axisCount = 0;
    newOptions.plugins.tooltip.callbacks.label = tooltipRender;
    newOptions.scales.currency.ticks.callback = currencyFormat;
    newOptions.scales.number.ticks.callback = numberFormat;
    newOptions.scales.percent.ticks.callback = percentFormat;
    chartMemoData?.datasets?.forEach(({ metricType, hidden }: any) => {
      switch (metricType) {
        case 'currency':
          if (!newOptions.scales.currency.display) {
            axisCount++;
            newOptions.scales.currency.position =
              axisCount % 2 === 0 ? 'right' : 'left';
            newOptions.scales.currency.display = !hidden;
          }

          break;
        case 'number':
          if (!newOptions.scales.number.display) {
            axisCount++;
            newOptions.scales.number.position =
              axisCount % 2 === 0 ? 'right' : 'left';
            newOptions.scales.number.display = !hidden;
          }

          break;
        case 'percent':
          if (!newOptions.scales.percent.display) {
            axisCount++;
            newOptions.scales.percent.position =
              axisCount % 2 === 0 ? 'right' : 'left';
            newOptions.scales.percent.display = !hidden;
          }

          break;
      }
    });

    return newOptions;
  }, [
    chartOptions,
    chartMemoData,
    currencyFormat,
    numberFormat,
    percentFormat,
    tooltipRender,
  ]);

  const onToggleVisibleMetric = useCallback(
    (_: any, __: any, searchIndex: never) => {
      if (invisibleMetrics.includes(searchIndex)) {
        setInvisibleMetrics(invisibleMetrics.filter(el => el !== searchIndex));
      } else {
        setInvisibleMetrics([...invisibleMetrics, searchIndex]);
      }
    },
    [setInvisibleMetrics, invisibleMetrics]
  );

  const handleShowMetricsModal = async () => {
    onShowMetricsModal({
      onSubmit: (_: any) => {
        setInvisibleMetrics([]);
      },
    });
  };

  return (
    <>
      <IonRow>
        <IonCol>
          <div className={'chips ion-padding-horizontal'}>
            <Button
              fill={'clear'}
              onClick={handleShowMetricsModal}
              className={'border button-radius'}
            >
              <IonIcon icon={ellipsisHorizontal} />
            </Button>

            {chartMemoData?.datasets?.map((el: any, index: never) => {
              return (
                <IonChip
                  key={index}
                  outline={el.hidden}
                  style={
                    el.hidden
                      ? {}
                      : { background: el.borderColor, color: 'white' }
                  }
                  onClick={e => onToggleVisibleMetric(e, el, index)}
                >
                  <Typography color={'dark-contrast'}>{el.label}</Typography>
                </IonChip>
              );
            })}
          </div>
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol className={'ion-padding'}>
          <div className={'chart-wrapper'}>
            <Line options={chartOptionsMemo} data={chartMemoData} />
          </div>
          {isRefetching && <ProgressBar type={'indeterminate'} />}
        </IonCol>
      </IonRow>
    </>
  );
};
export default Chart;
